import type { Mass, Unit } from './mass.ts';

/** A plate denomination and how many of them are available for ONE side of the bar. */
export interface PlateStock {
  weight: Mass;
  perSide: number;
}

export interface Gym {
  unit: Unit;
  bar: Mass;
  /** Weight of one collar; two are used. */
  collar: Mass;
  plates: readonly PlateStock[];
}

/** Plates on one side, innermost (heaviest) first. */
export type Loading = readonly Mass[];

export interface PlateDelta {
  remove: Loading;
  add: Loading;
  /** Plates that stay on the bar, innermost first. */
  keep: Loading;
}

const MAX_SOLUTIONS = 200;

function sortedStock(gym: Gym): PlateStock[] {
  return [...gym.plates].filter((p) => p.perSide > 0 && p.weight > 0).sort((a, b) => b.weight - a.weight);
}

function perSideTarget(gym: Gym, total: Mass): number | null {
  const rest = total - gym.bar - 2 * gym.collar;
  if (rest < 0 || rest % 2 !== 0) return null;
  return rest / 2;
}

/** Every way to make `side` from the stock, as heaviest-first lists (bounded). */
function solutions(stock: PlateStock[], side: number): Mass[][] {
  const out: Mass[][] = [];
  const cur: Mass[] = [];
  // What the plates from i onwards can add at most: an unreachable remainder is abandoned at once instead of
  // exploring every combination (an absurd or unloadable target used to take seconds per warm-up set).
  const capacity = stock.map((_, i) => stock.slice(i).reduce((n, p) => n + p.weight * p.perSide, 0));
  const walk = (i: number, left: number): void => {
    if (out.length >= MAX_SOLUTIONS) return;
    if (left === 0) {
      out.push([...cur]);
      return;
    }
    if (i >= stock.length || left > capacity[i]!) return;
    const p = stock[i]!;
    const maxN = Math.min(p.perSide, Math.floor(left / p.weight));
    for (let n = maxN; n >= 0; n--) {
      for (let k = 0; k < n; k++) cur.push(p.weight);
      walk(i + 1, left - n * p.weight);
      cur.length -= n;
    }
  };
  walk(0, side);
  return out;
}

/** Fewest plates for `total`, or null if it can't be loaded exactly. Empty array = just the bar (and collars). */
export function loadFor(gym: Gym, total: Mass): Loading | null {
  const side = perSideTarget(gym, total);
  if (side === null) return null;
  const all = solutions(sortedStock(gym), side);
  if (all.length === 0) return null;
  return all.reduce((best, s) => (s.length < best.length ? s : best));
}

export function isLoadable(gym: Gym, total: Mass): boolean {
  return loadFor(gym, total) !== null;
}

/** All loadable totals from the bar up to `max`, ascending. */
export function loadableTotals(gym: Gym, max: Mass): Mass[] {
  const stock = sortedStock(gym);
  const base = gym.bar + 2 * gym.collar;
  const sides = new Set<number>([0]);
  for (const p of stock) {
    const next = new Set<number>();
    for (const s of sides) for (let n = 0; n <= p.perSide; n++) next.add(s + n * p.weight);
    sides.clear();
    for (const s of next) if (base + 2 * s <= max) sides.add(s);
  }
  return [...sides].map((s) => base + 2 * s).sort((a, b) => a - b);
}

/** Heaviest loadable total ≤ `target` (never below the bar). Null only if even the bar exceeds `target`. */
export function floorToLoadable(gym: Gym, target: Mass): Mass | null {
  const totals = loadableTotals(gym, target);
  return totals.length ? totals[totals.length - 1]! : null;
}

/** Lightest loadable total strictly above `w`, or null if nothing heavier can be built. */
export function nextLoadableAbove(gym: Gym, w: Mass): Mass | null {
  const stock = sortedStock(gym);
  const maxSide = stock.reduce((s, p) => s + p.weight * p.perSide, 0);
  const ceiling = gym.bar + 2 * gym.collar + 2 * maxSide;
  const above = loadableTotals(gym, ceiling).find((t) => t > w);
  return above ?? null;
}

function commonPrefix(a: Loading, b: Loading): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
}

/**
 * Plate change from `from` to `to`. Plates come off from the outside, so only an inner prefix shared
 * with the new loading can stay. Cost = plates moved + plates left on the bar: minimising moves alone
 * piles up small plates across a warm-up (found via the design data pack, 13 Oct 2026), and lifters
 * load largest-first. Ties prefer fewer plates, then fewer moves.
 */
export function plateDelta(gym: Gym, from: Loading, to: Mass): PlateDelta | null {
  const side = perSideTarget(gym, to);
  if (side === null) return null;
  const all = solutions(sortedStock(gym), side);
  if (all.length === 0) return null;
  let best: { delta: PlateDelta; cost: number; plates: number; moves: number } | null = null;
  for (const s of all) {
    const k = commonPrefix(from, s);
    const delta: PlateDelta = { keep: s.slice(0, k), remove: from.slice(k), add: s.slice(k) };
    const moves = delta.remove.length + delta.add.length;
    const cost = moves + s.length;
    const better = !best || cost < best.cost || (cost === best.cost && (s.length < best.plates || (s.length === best.plates && moves < best.moves)));
    if (better) best = { delta, cost, plates: s.length, moves };
  }
  return best!.delta;
}
